import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ResponsivePie } from '@nivo/pie';
import { useCSATRatingDistribution } from '@/hooks/useCSATRatingDistribution';
import { chartColors, statusColors } from '@/theme/colors';

interface RatingDistributionChartProps {
  daysRange: number;
}

const getRatingColor = (rating: number): string => {
  switch (rating) {
    case 5:
      return statusColors.success; // Verde - Excelente
    case 4:
      return statusColors.success; // Verde - Bom
    case 3:
      return statusColors.delay; // Amarelo - Neutro
    case 2:
      return statusColors.failed; // Vermelho - Ruim
    case 1:
      return statusColors.failed; // Vermelho - Muito ruim
    default:
      return chartColors.neutral;
  }
};

const getRatingLabel = (rating: number): string => {
  return '⭐'.repeat(rating);
};

export const RatingDistributionChart = ({ daysRange }: RatingDistributionChartProps) => {
  const { data, isLoading } = useCSATRatingDistribution(daysRange);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribuição de Notas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0 || data.every(d => d.count === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribuição de Notas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <p>Nenhum dado disponível</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data
    .filter(d => d.count > 0)
    .map(d => ({
      id: `${d.rating} estrelas`,
      label: getRatingLabel(d.rating),
      value: d.percentage,
      count: d.count,
      rating: d.rating,
    }))
    .reverse(); // Inverter para mostrar 5 estrelas primeiro

  const totalResponses = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribuição de Notas</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="w-full min-h-[250px] max-h-[350px] overflow-hidden">
          <div className="w-full h-[250px] sm:h-[300px] md:h-[350px]">
            <ResponsivePie
              data={chartData}
              margin={{ top: 10, right: 10, bottom: 50, left: 10 }}
            innerRadius={0.5}
            padAngle={2}
            cornerRadius={4}
            activeOuterRadiusOffset={6}
            colors={(d) => getRatingColor(d.data.rating)}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
            arcLinkLabelsSkipAngle={15}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={1.5}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={15}
            arcLabelsTextColor="#ffffff"
            arcLabelsRadiusOffset={0.5}
            tooltip={({ datum }) => (
              <div className="bg-white p-2 rounded shadow-lg border text-xs">
                <div className="font-semibold text-sm">{datum.label}</div>
                <div className="text-xs text-muted-foreground">
                  {datum.data.count} respostas ({datum.value}%)
                </div>
              </div>
            )}
            legends={[
              {
                anchor: 'bottom',
                direction: 'row',
                justify: false,
                translateX: 0,
                translateY: 40,
                itemsSpacing: 40,
                itemWidth: 60,
                itemHeight: 14,
                itemTextColor: '#333',
                itemDirection: 'left-to-right',
                itemOpacity: 1,
                symbolSize: 8,
                symbolShape: 'circle',
              },
            ]}
            />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 sm:gap-2">
          {data.map((item) => (
            <div
              key={item.rating}
              className="text-center p-1 sm:p-1.5 rounded border bg-muted/50"
            >
              <div className="text-xs sm:text-sm font-semibold mb-0.5">
                {getRatingLabel(item.rating)}
              </div>
              <div className="text-base sm:text-lg md:text-xl font-bold text-foreground">
                {item.percentage}%
              </div>
              <div className="text-[9px] sm:text-[10px] text-muted-foreground">
                {item.count} {item.count === 1 ? 'resposta' : 'respostas'}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

